import { useEffect, useRef, useState } from 'react';

import type { CanvasTab } from './CanvasProvider';

/** Default milliseconds between typewriter ticks. */
export const CANVAS_TYPEWRITER_SPEED_MS = 15;

/**
 * How long the canvas should keep `isStreaming` true so the local typewriter
 * can finish revealing `text` without snapping.
 */
export function canvasTypewriterWaitMs(
  text: string,
  speed = CANVAS_TYPEWRITER_SPEED_MS,
): number {
  const n = Math.max(0, text.length);
  const charsPerTick = n > 600 ? 6 : n > 240 ? 4 : n > 80 ? 2 : 1;
  const ticks = Math.ceil(n / charsPerTick);
  return Math.min(Math.max(ticks * speed + 100, 300), 1800);
}

export function waitForCanvasTypewriter(
  text: string,
  speed = CANVAS_TYPEWRITER_SPEED_MS,
): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, canvasTypewriterWaitMs(text, speed));
  });
}

export function canvasTabForStreamingSection(section: string): CanvasTab {
  const s = section.trim().toLowerCase();
  if (s === 'cover_letter') return 'cover_letter';
  if (s === 'application_note') return 'application_note';
  return 'resume';
}

/** Focus & Auto-Scroll Controller — lock the viewport onto the generating canvas. */
export function focusCanvasPreview(tab?: CanvasTab): void {
  window.dispatchEvent(
    new CustomEvent('ggj:canvas-focus', { detail: { tab } }),
  );
}

export function useCanvasTypewriter(
  content: string,
  options?: { isStreaming?: boolean; speed?: number },
): { displayedText: string; isTyping: boolean } {
  const isStreaming = options?.isStreaming === true;
  const speed = options?.speed ?? CANVAS_TYPEWRITER_SPEED_MS;
  const safeContent = content ?? '';

  const [displayedText, setDisplayedText] = useState(() =>
    isStreaming ? '' : safeContent,
  );

  const indexRef = useRef(isStreaming ? 0 : safeContent.length);
  const contentRef = useRef(safeContent);
  const streamingRef = useRef(isStreaming);

  useEffect(() => {
    const wasStreaming = streamingRef.current;
    const prevContent = contentRef.current;
    streamingRef.current = isStreaming;
    contentRef.current = safeContent;

    if (isStreaming && !wasStreaming) {
      indexRef.current = 0;
      setDisplayedText('');
      return;
    }

    if (!isStreaming && wasStreaming) {
      indexRef.current = safeContent.length;
      setDisplayedText(safeContent);
      return;
    }

    if (
      isStreaming &&
      prevContent &&
      safeContent &&
      !safeContent.startsWith(prevContent) &&
      !prevContent.startsWith(safeContent)
    ) {
      indexRef.current = 0;
      setDisplayedText('');
    }
  }, [safeContent, isStreaming]);

  useEffect(() => {
    if (indexRef.current >= safeContent.length) {
      if (displayedText !== safeContent) {
        setDisplayedText(safeContent);
        indexRef.current = safeContent.length;
      }
      return;
    }

    const remaining = safeContent.length - indexRef.current;
    const chunk = remaining > 600 ? 8 : remaining > 240 ? 5 : remaining > 80 ? 3 : 1;

    const timeout = window.setTimeout(() => {
      const next = Math.min(safeContent.length, indexRef.current + chunk);
      indexRef.current = next;
      setDisplayedText(safeContent.slice(0, next));
    }, speed);

    return () => window.clearTimeout(timeout);
  }, [displayedText, safeContent, speed]);

  return {
    displayedText,
    isTyping: displayedText.length < safeContent.length || (isStreaming && !safeContent),
  };
}
